import {
  validatePaginatedResponseWithSchema,
  validateWithSchema,
} from "@/helpers/common";
import { formatString } from "@/helpers/formatting";
import {
  borrowerPlatformTemplateListItemSchema,
  borrowerPlatformTemplateSchema,
} from "@/models/borrowerPlatformTemplates";
import { axiosClient } from "@/services/client";
import endpoints from "@/services/endpoints";

import type {
  BorrowerPlatformTemplate,
  BorrowerPlatformTemplateFile,
  BorrowerPlatformTemplateFilters,
  BorrowerPlatformTemplateListItem,
  CreateBorrowerPlatformTemplateRequest,
  UpdateBorrowerPlatformTemplateRequest,
} from "@/models/borrowerPlatformTemplates";
import type { IPaginatedResponse } from "@/models/common";

class BorrowerPlatformTemplateService {
  async getBorrowerPlatformTemplates(values: {
    payload: BorrowerPlatformTemplateFilters;
  }) {
    const response = await axiosClient.get<
      IPaginatedResponse<BorrowerPlatformTemplateListItem>
    >(endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.ALL, {
      params: values.payload,
    });

    return validatePaginatedResponseWithSchema(
      borrowerPlatformTemplateListItemSchema,
      response.data
    );
  }

  async getBorrowerPlatformTemplate(id: string) {
    const response = await axiosClient.get<{ data: BorrowerPlatformTemplate }>(
      formatString(endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.SINGLE, id)
    );
    return validateWithSchema(
      borrowerPlatformTemplateSchema,
      response.data.data
    );
  }

  async createBorrowerPlatformTemplate(values: {
    payload: CreateBorrowerPlatformTemplateRequest;
  }) {
    const response = await axiosClient.post<{
      data: BorrowerPlatformTemplate;
    }>(endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.ALL, values.payload);
    return validateWithSchema(
      borrowerPlatformTemplateSchema,
      response.data.data
    );
  }

  async updateBorrowerPlatformTemplate(values: {
    payload: UpdateBorrowerPlatformTemplateRequest;
  }) {
    if (!values.payload.id) {
      return;
    }
    const response = await axiosClient.put<{ data: BorrowerPlatformTemplate }>(
      formatString(
        endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.SINGLE,
        values.payload.id
      ),
      values.payload
    );
    return validateWithSchema(
      borrowerPlatformTemplateSchema,
      response.data.data
    );
  }

  async deleteBorrowerPlatformTemplate(id: string) {
    const response = await axiosClient.delete<{ data: { deleted: boolean } }>(
      formatString(endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.SINGLE, id)
    );
    return validateWithSchema(borrowerPlatformTemplateSchema, response.data);
  }

  async duplicateBorrowerPlatformTemplate(id: string) {
    // For duplicate, we'll get the original template and create a new one with modified name
    const original = await this.getBorrowerPlatformTemplate(id);
    const currentTime = new Date().toISOString();
    const duplicatePayload: CreateBorrowerPlatformTemplateRequest & {
      created_at: string;
      updated_at: string;
    } = {
      name: `${original.name} (Copy)`,
      description: original.description ?? "",
      content: original.content,
      is_active: false, // Set as inactive by default
      created_at: currentTime,
      updated_at: currentTime,
    };

    const response = await axiosClient.post<{
      data: BorrowerPlatformTemplate;
    }>(endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.ALL, duplicatePayload);
    return validateWithSchema(
      borrowerPlatformTemplateSchema,
      response.data.data
    );
  }

  async toggleGlobalStatus(id: string) {
    const response = await axiosClient.put<{ data: BorrowerPlatformTemplate }>(
      formatString(
        endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.TOGGLE_GLOBAL,
        id
      )
    );
    return validateWithSchema(
      borrowerPlatformTemplateSchema,
      response.data.data
    );
  }

  async uploadFile(bptId: string, file: File) {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axiosClient.post<{
      data: BorrowerPlatformTemplateFile;
    }>(
      formatString(endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.FILES, bptId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data.data;
  }

  async getFile(bptId: string, fileId: string | number) {
    const response = await axiosClient.get<{
      data: BorrowerPlatformTemplateFile;
    }>(
      formatString(
        endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.SINGLE_FILE,
        bptId,
        fileId
      )
    );
    return response.data.data;
  }

  async deleteFile(bptId: string, fileId: string) {
    const response = await axiosClient.delete<{ data: { deleted: boolean } }>(
      formatString(
        endpoints.CLIENTS.BORROWER_PLATFORM_TEMPLATES.SINGLE_FILE,
        bptId,
        fileId
      )
    );
    return response.data;
  }
}

const borrowerPlatformTemplateService = new BorrowerPlatformTemplateService();
export default borrowerPlatformTemplateService;
